import { ControlledInput, FlyupEditingModal } from "shared/components";
import { useTranslation } from "react-i18next";
import { GitMerge, MinusCircle } from "iconoir-react";
import { Flex } from "styled-system/jsx";
import { Button, Text, useToastContext } from "@ttc3k/trekker";
import { TagByIdQuery } from "gql/generated";
import { useTagSplitFlyupForm } from "./useTagSplitFlyupForm";
import { FormProvider, useWatch } from "react-hook-form";
import { useEffect } from "react";
import { getMultilingualString, joinWithAnd } from "shared/utils";
import { InternalRefetchQueriesInclude } from "@apollo/client";

type TagSplitFlyupProps = {
  tag: TagByIdQuery["tagById"];
  isOpen: boolean;
  onClose: () => void;
  onTagSplitCompleted: (
    updatedTag: TagByIdQuery["tagById"],
    newTags: TagByIdQuery["tagById"][],
  ) => void;
  refetchQueries?: InternalRefetchQueriesInclude;
};

export const TagSplitFlyup = ({
  tag,
  isOpen,
  onClose,
  onTagSplitCompleted,
  refetchQueries,
}: TagSplitFlyupProps) => {
  const { t } = useTranslation("adminland", {
    keyPrefix: "PAGE.TAG_MANAGER.FLYOUTS.TAG_DETAILS.TAG_SPLIT_MODAL",
  });
  const { t: tCore } = useTranslation("core");
  const { toastFactory } = useToastContext();

  const { form, onSubmit } = useTagSplitFlyupForm({
    tag,
    onSuccess: (
      updatedExistingTag: TagByIdQuery["tagById"],
      newTags: TagByIdQuery["tagById"][],
    ) => {
      toastFactory.create({
        title: tCore("SUCCESS"),
        description: t("SUCCESS", {
          label: getMultilingualString(tag?.name ?? {}),
          newTags: joinWithAnd(
            [
              getMultilingualString(updatedExistingTag?.name ?? {}),
              ...newTags.map(tag => getMultilingualString(tag?.name ?? {})),
            ],
            tCore("AND"),
          ),
        }),
      });
      onTagSplitCompleted(updatedExistingTag, newTags);
      onClose();
      setTimeout(() => form.reset(), 350);
    },
    onError: () => {
      toastFactory.create({
        title: tCore("ERROR.TITLE"),
        description: t("ERRORS.TAG_SPLIT"),
      });
    },
    refetchQueries,
  });

  const nameForCurrentTag = useWatch({
    control: form.control,
    name: "nameForCurrentTag",
  });

  const namesForNewTags = useWatch({
    control: form.control,
    name: "namesForNewTags",
  });

  useEffect(() => {
    if (nameForCurrentTag === getMultilingualString(tag?.name ?? {})) {
      form.setError("nameForCurrentTag", {
        message: t("ERRORS.NAME_MUST_BE_DIFFERENT"),
      });
    }
  }, [form, nameForCurrentTag, t, tag]);

  return (
    <FlyupEditingModal
      isOpen={isOpen}
      onClose={onClose}
      title={t("TITLE")}
      HeaderIcon={GitMerge}
      headerIconProps={{ size: "lg" }}
      cancelButtonProps={{
        mode: "light",
        children: t("CTA_BUTTONS.CANCEL"),
        onClick: onClose,
        type: "button",
      }}
      saveButtonProps={{
        children: t("CTA_BUTTONS.SPLIT"),
        visual: "accent.blue",
        disabled: !form.formState.isValid,
        isLoading: form.formState.isSubmitting,
        onClick: onSubmit,
        type: "button",
      }}
    >
      <FormProvider {...form}>
        <form id="tag-split-flyup-form">
          <Flex flexDir="column" gap="200">
            <Text>{t("DESCRIPTION")}</Text>
            <ControlledInput
              name="nameForCurrentTag"
              label={t("NEW_NAME_FOR_CURRENT_TAG")}
            />
            {namesForNewTags.map((_, index) => (
              <Flex key={index} alignItems="center" gap="100">
                <ControlledInput
                  key={index}
                  name={`namesForNewTags.${index}`}
                  label={t("NAME_FOR_NEW_TAG")}
                />
                <Button
                  visual="unstyled"
                  Icon={MinusCircle}
                  color="icon.light"
                  mt="18px"
                  disabled={namesForNewTags.length === 1 && index === 0}
                  onClick={() =>
                    form.setValue(
                      "namesForNewTags",
                      namesForNewTags.filter((_, i) => i !== index),
                      {
                        shouldDirty: true,
                      },
                    )
                  }
                  type="button"
                  _hover={{
                    color: "icon.mid",
                    _disabled: {
                      color: "icon.light",
                    },
                  }}
                  _focusVisible={{
                    color: "icon.mid",
                    _disabled: {
                      color: "icon.light",
                    },
                  }}
                />
              </Flex>
            ))}
            <Button
              visual="unstyled"
              color="text.accent.blue.mid"
              _hover={{
                color: "text.accent.blue.dark",
              }}
              _focusVisible={{
                color: "text.accent.blue.dark",
              }}
              onClick={() =>
                form.setValue("namesForNewTags", [...namesForNewTags, ""], {
                  shouldDirty: true,
                })
              }
              type="button"
              width="fit-content"
            >
              + {t("CTA_BUTTONS.ADD_ANOTHER_TAG_NAME")}
            </Button>
          </Flex>
        </form>
      </FormProvider>
    </FlyupEditingModal>
  );
};
